import type { CmsItem, LocalizedSlugMap } from '~/types/cms';

type LocaleWithCraft = {
    code: string;
    language?: string;
    craftSiteId?: number;
};

function isNavigableSlug(slug: unknown): slug is string {
    if (typeof slug !== 'string') {
        return false;
    }

    const normalized = slug
        .trim()
        .replace(/^\/+|\/+$/g, '')
        .toLowerCase();
    return normalized.length > 0 && normalized !== 'home';
}

function localeForLocalization(locales: LocaleWithCraft[], localization: CmsItem): LocaleWithCraft | undefined {
    const locLang = localization?.language as string | undefined;
    const locSiteId = localization?.siteId as number | undefined;

    return locales.find(locale => {
        if (locLang && locale.language === locLang) {
            return true;
        }
        if (locLang && locale.language) {
            const a = locLang.toLowerCase().split('-')[0];
            const b = locale.language.toLowerCase().split('-')[0];
            if (a.length && a === b) {
                return true;
            }
        }
        if (locSiteId != null && locale.craftSiteId != null && locSiteId === locale.craftSiteId) {
            return true;
        }
        return false;
    });
}

export function resolveLocalizedSlug(data: CmsItem, list: LocaleWithCraft[]): LocalizedSlugMap {
    const slugify: LocalizedSlugMap = Object.fromEntries(list.map(locale => [locale.code, {}]));

    if (data.language && isNavigableSlug(data.slug)) {
        const locale = localeForLocalization(list, { language: data.language, siteId: data.siteId });
        if (locale?.code) {
            slugify[locale.code] = { slug: data.slug };
        }
    }

    if (Array.isArray(data.localized)) {
        for (const localization of data.localized) {
            const currentLanguage = localeForLocalization(list, localization);

            if (isNavigableSlug(localization?.slug) && currentLanguage?.code) {
                slugify[currentLanguage.code] = {
                    slug: localization.slug
                };
            }
        }
    }

    return slugify;
}

export function useLocalizedSlug(data: CmsItem): LocalizedSlugMap {
    const { locales } = useI18n();

    return resolveLocalizedSlug(data, locales.value as LocaleWithCraft[]);
}
